import type { DependencyList } from 'react';
import type { ConnectionStatus } from '~/channels/connectionStatus';
import {
  type BaseEventTypes,
  createEventEmitter,
  dispatchEvent,
  type EventListener,
  useEvent,
} from '~/lib/customEvents';
import type { GlobalStoreTypes } from '~/lib/globalStore';
import type { Toast } from '~/lib/types';
import type { UploadProgressEvent } from '~/lib/uploadFile';

export type GlobalEventTypes = BaseEventTypes & {
  'document:delete': [{ documentId: number }];
  's3File:uploadProgress': [
    { s3FileId: number; progressEvent: UploadProgressEvent },
  ];
  's3File:uploadComplete': [{ s3FileId: number }];
  's3File:delete': [{ s3FileId: number }];
  'toast:upsert': [Toast];
  'toast:close': [string];
  'toast:reopen': [string];
  'modal:open': [string];
  'modal:close': [string];
  'formattingToolbar:toggle': [];
  connectionStatusChanged: [ConnectionStatus];
} & {
  [K in keyof GlobalStoreTypes as `store:${K}`]: [GlobalStoreTypes[K]];
};

export const globalEventEmitter = createEventEmitter<GlobalEventTypes>();

export const useGlobalEvent = <K extends keyof GlobalEventTypes>(
  eventName: K,
  handler: EventListener<GlobalEventTypes[K]>,
  deps?: DependencyList
) => useEvent(globalEventEmitter, eventName, handler, deps);

export const dispatchGlobalEvent = <K extends keyof GlobalEventTypes>(
  eventName: K,
  ...args: GlobalEventTypes[K]
) => dispatchEvent(globalEventEmitter, eventName, ...args);
